import { BOTE } from '@somnhold/content';
import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, imax, imin, isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng, type RngState } from '../math/rng.ts';
import type { Pt } from '../caza-types.ts';
import type { WakeChannel, WakeFork } from '../bote-types.ts';

/**
 * The Long Hunt's chapter 3 grounds from the seed (docs/sendas/barbaro.md, «El bote»): Bera's long boat in the middle of
 * the board, bow to the north, with its six oars, its shields on the gunwale and Bera at the tiller; the wake's forks
 * (act 1), the floes off its sides crossed to by the bow rope (act 2), and the floes joined to it by necks of ice with
 * the dark water off their tips (act 3). The fight's builder (campaign/sendas-caza.ts) and the renderer build them the
 * same way. Integer maths only.
 */

export const BOTE_RADIUS = 11;

/** The hull's rows (r) from bow to stern; how wide each is (|x| at most, sim units). */
const HULL_ROWS = 5;
const hullHalf = (r: number): number => (Math.abs(r) >= 4 ? 500 : 1000);

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

const key = (h: Hex): string => `${h.q},${h.r}`;
const len = (dx: number, dy: number): number => isqrt(dx * dx + dy * dy);

/** The hull's cells: rows −5 to 5, two or three across (one or two at bow and stern). */
export const hullCells = (): Hex[] => {
  const out: Hex[] = [];
  for (let r = -HULL_ROWS; r <= HULL_ROWS; r++)
    for (let q = -8; q <= 8; q++) {
      const p = hexToWorld(q, r);
      if (Math.abs(p.x) <= hullHalf(r)) out.push({ q, r });
    }
  return out;
};

export interface BoatLayout {
  hull: Hex[];
  mid: Pt;
  stern: number;
  bow: number;
  oars: Pt[];
  shields: Pt[];
  tiller: Pt;
}

/** The boat: the same in every act. Oars at rows −2, 0, 2 on both sides (west, east by turns, bow first). */
export const boatLayout = (): BoatLayout => {
  const ROW = hexToWorld(0, 1).y;
  const oars: Pt[] = [];
  for (const r of [-2, 0, 2]) for (const k of [-1, 1]) oars.push({ x: k * 1000, y: r * ROW });
  const shields: Pt[] = [];
  for (const r of [-3, -1, 1, 3]) for (const k of [-1, 1]) shields.push({ x: k * 500, y: r * ROW });
  return { hull: hullCells(), mid: { x: 0, y: 0 }, stern: HULL_ROWS * ROW + 350, bow: -HULL_ROWS * ROW - 350, oars, shields, tiller: { x: 0, y: HULL_ROWS * ROW - 200 } };
};

/** The board's rocks: every cell of the map (its radius + 2) that is not floor. */
const rocksBut = (R: number, floor: Set<string>): Hex[] => allCells(R + 2).filter((h) => !floor.has(key(h)));

/** The cells within `r` hexes of a point's hex. */
const blob = (at: Pt, r: number): Hex[] => {
  const out: Hex[] = [];
  for (const h of allCells(BOTE_RADIUS)) {
    const p = hexToWorld(h.q, h.r);
    if (len(p.x - at.x, p.y - at.y) <= r * 1000 + 150) out.push(h);
  }
  return out;
};

/* ---------- the wake (act 1) ---------- */

export interface WakeLayout {
  rocks: Hex[];
  boat: BoatLayout;
  forks: WakeFork[];
  start: Pt;
}

/** Whether a fork reads one way only: exactly one channel with the Wolf's wake that the current doesn't close. */
export const forkGood = (f: WakeFork): boolean => {
  const ok = f.channels.map((c, i) => (c.ash && c.drift >= 0 ? i : -1)).filter((i) => i >= 0);
  return ok.length === 1 && ok[0] === f.right && f.channels.every((c) => c.wide <= (f.channels[f.wide] as WakeChannel).wide);
};

const pick = <T,>(rng: RngState, xs: readonly T[]): T => xs[rngInt(rng, xs.length)] as T;

/**
 * A fork from the seed. The right one runs the Wolf's wake and stays open; the widest (Ivar's) the current closes, now
 * with his wake in it (he went through before it shut), now with a smear only; the third is the pack's, the most marked
 * (they're many), with ash smeared at its mouth where they swam over his. Once in the act the widest is the right one
 * (`wideRight`): the rule is the wake and the current, not «never the wide one».
 */
const fork = (rng: RngState, wideRight: boolean, teach: boolean): WakeFork => {
  const lanes = [-1, 0, 1];
  const right = rngInt(rng, 3);
  const ch: WakeChannel[] = lanes.map((lane) => ({ lane, wide: 1, drift: 0, ash: false, smear: false, pack: 0 }));
  const R = ch[right] as WakeChannel;
  if (wideRight) {
    Object.assign(R, { wide: 3, drift: 1, ash: true, pack: 1 });
    const others = [0, 1, 2].filter((i) => i !== right);
    const a = ch[others[0] as number] as WakeChannel;
    const b = ch[others[1] as number] as WakeChannel;
    // One with his wake in it too, closing; one the pack's, smeared.
    Object.assign(a, { wide: 2, drift: -1, ash: true, pack: 1 });
    Object.assign(b, { wide: 1, drift: pick(rng, [0, 1]), smear: true, pack: 4 });
    return { channels: ch, right, wide: right };
  }
  const wideAt = (right + 1 + rngInt(rng, 2)) % 3;
  const third = 3 - right - wideAt;
  Object.assign(R, { wide: 1 + rngInt(rng, 2), drift: teach ? 1 : pick(rng, [0, 1, 1]), ash: true, pack: rngInt(rng, 2) });
  const W = ch[wideAt] as WakeChannel;
  // The wide one closing: with his wake (he went in before it shut) half the time, or only a smear at its mouth.
  const withAsh = !teach && rngInt(rng, 2) === 0;
  Object.assign(W, { wide: 3, drift: -1, ash: withAsh, smear: !withAsh, pack: 1 + rngInt(rng, 2) });
  const P = ch[third] as WakeChannel;
  Object.assign(P, { wide: 1 + rngInt(rng, 2), drift: pick(rng, [0, 1]), smear: !teach, pack: 3 + rngInt(rng, 2) });
  return { channels: ch, right, wide: wideAt };
};

export const wakeLayout = (seed: number): WakeLayout => {
  const rng = seedRng(deriveSeed(seed, 0xb07e));
  const boat = boatLayout();
  const n = BOTE.wake.forks;
  // The widest is the right one once, never at the first fork (it teaches) nor the last.
  const wr = 2 + rngInt(rng, imax(1, n - 3));
  const forks: WakeFork[] = [];
  for (let i = 0; i < n; i++) {
    let f = fork(rng, i === wr, i === 0);
    for (let k = 0; k < 8 && !forkGood(f); k++) f = fork(rng, i === wr, i === 0);
    forks.push(f);
  }
  const floor = new Set(boat.hull.map(key));
  return { rocks: rocksBut(BOTE_RADIUS, floor), boat, forks, start: { x: 0, y: -1700 } };
};

/* ---------- the swell (act 2) ---------- */

export interface SwellLayout {
  rocks: Hex[];
  boat: BoatLayout;
  floes: Pt[];
  floeCells: Hex[][];
  /** The big waves' times (waveT). */
  plan: number[];
  start: Pt;
}

export const swellLayout = (seed: number): SwellLayout => {
  const rng = seedRng(deriveSeed(seed, 0x5e11));
  const boat = boatLayout();
  const ROW = hexToWorld(0, 1).y;
  // Four floes off the sides, two cells of sea between them and the gunwale; a little off square, from the seed.
  const floes: Pt[] = [];
  for (const [kx, ky] of [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ] as const) {
    const h = hexToWorld(0, ky * (2 + rngInt(rng, 2)));
    floes.push({ x: kx * (4000 + rngInt(rng, 2) * 500) + (h.x % 1000), y: h.y });
  }
  const floeCells = floes.map((p) => blob(p, 1));
  const floor = new Set([...boat.hull, ...floeCells.flat()].map(key));
  const W = BOTE.swell;
  const plan: number[] = [];
  let t = W.first;
  for (let i = 0; i < W.need + 2; i++) {
    plan.push(t);
    t += W.every[0] + rngInt(rng, W.every[1] - W.every[0] + 1);
  }
  void ROW;
  return { rocks: rocksBut(BOTE_RADIUS, floor), boat, floes, floeCells, plan, start: { x: 0, y: -900 } };
};

/* ---------- the old howler (act 3) ---------- */

export interface ElderLayout {
  rocks: Hex[];
  boat: BoatLayout;
  floes: Pt[];
  floeCells: Hex[][];
  /** The necks of ice joining each floe to the gunwale. */
  necks: Hex[][];
  tips: Pt[];
  deep: Pt[];
  /** The dark water's turns: a mask of two floes each. */
  dark: number[];
  start: Pt;
}

/** The cells within `w` of a segment. */
const band = (a: Pt, b: Pt, w: number): Hex[] => {
  const out: Hex[] = [];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = imax(1, dx * dx + dy * dy);
  for (const h of allCells(BOTE_RADIUS)) {
    const p = hexToWorld(h.q, h.r);
    let t = idiv(((p.x - a.x) * dx + (p.y - a.y) * dy) * 100, imax(1, idiv(l2, 100)));
    t = imax(0, imin(10000, t));
    const x = a.x + idiv(dx * t, 10000);
    const y = a.y + idiv(dy * t, 10000);
    if (len(p.x - x, p.y - y) <= w) out.push(h);
  }
  return out;
};

export const elderLayout = (seed: number): ElderLayout => {
  const rng = seedRng(deriveSeed(seed, 0xe1de));
  const boat = boatLayout();
  // Two long floes, one off each side, joined to the gunwale by a neck of ice amidships; each has a tip at its north
  // and its south end, and the dark water beyond. Tips: 0 north-west, 1 north-east, 2 south-west, 3 south-east.
  const floes: Pt[] = [];
  const tips: Pt[] = [];
  const deep: Pt[] = [];
  const necks: Hex[][] = [];
  const floeCells: Hex[][] = [];
  const xs = [-1, 1].map((k) => k * (5000 + rngInt(rng, 2) * 500));
  const len0 = 4300 + rngInt(rng, 3) * 300;
  for (const ky of [-1, 1])
    for (const x of xs) {
      floes.push({ x, y: ky * idiv(len0, 2) });
      tips.push({ x: x + (x > 0 ? 300 : -300), y: ky * (len0 + 700) });
      deep.push({ x: x + (x > 0 ? 900 : -900), y: ky * (len0 + 3000) });
    }
  for (const x of xs) {
    const k = x > 0 ? 1 : -1;
    const c = band({ x, y: -len0 }, { x, y: len0 }, 1250);
    for (const ky of [-1, 1]) for (const h of blob({ x: x + k * 300, y: ky * (len0 + 700) }, 0)) if (!c.some((y) => y.q === h.q && y.r === h.r)) c.push(h);
    floeCells.push(c);
    necks.push(band({ x: k * 900, y: 0 }, { x: x - k * 1000, y: 0 }, 520));
  }
  // The dark water by turns: two floes at a time, never the same two twice running.
  // (Masks of the tips: 3 the north pair, 12 the south, 5 the west, 10 the east, 6 and 9 across.)
  const pairs = [3, 5, 6, 9, 10, 12];
  const dark: number[] = [];
  let last = -1;
  for (let i = 0; i < 40; i++) {
    let m = pairs[rngInt(rng, pairs.length)] as number;
    if (m === last) m = pairs[(pairs.indexOf(m) + 1 + rngInt(rng, 5)) % pairs.length] as number;
    dark.push(m);
    last = m;
  }
  const floor = new Set([...boat.hull, ...floeCells.flat(), ...necks.flat()].map(key));
  return { rocks: rocksBut(BOTE_RADIUS, floor), boat, floes, floeCells, necks, tips, deep, dark, start: { x: 0, y: 900 } };
};
